import React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import type { SelectProps } from '@radix-ui/react-select';
import {
    flattenGroups,
    groupNormalizedOptions,
    normalizeOption,
    normalizeOptions,
    normalizeOptionGroup,
    type NormalizedOption,
    type NormalizedOptionGroups,
    type OptionGroupingStrategy,
    type Options,
    type OptionProp,
    type OptionGroup,
    type OptionsGroup,
} from '../shared/normalize-options';
import { SelectTrigger } from './components/select-trigger';
import { SelectDropdownContent } from './components/select-dropdown-content';
import { SelectItem } from './components/select-item';

type SelectChangeDetails = {
    value: string;
    name?: string;
    option?: OptionProp;
};

type SelectChangeHandler = (details: SelectChangeDetails) => void;

type SupportedRootProps = Pick<SelectProps, 'value' | 'defaultValue' | 'open' | 'defaultOpen' | 'onOpenChange' | 'dir' | 'autoComplete' | 'disabled' | 'required' | 'form'>;

type SelectInputProps = SupportedRootProps & {
    /**
     * options that will be rendered as children, you can also construct your option markup as children
     */
    label?: string;
    options?: Options;
    placeholder?: string;
    className?: string;
    style?: React.CSSProperties;
    name?: string;
    id?: string;
    'aria-describedby'?: string;
    'data-testid'?: string;
    optionGroupStrategy?: OptionGroupingStrategy;
    onSelectionChange?: SelectChangeHandler;
    /** @deprecated use `onSelectionChange` */
    onChange?: SelectChangeHandler;
    onValueChange?: SelectProps['onValueChange'];
    onBlur?: React.FocusEventHandler<HTMLButtonElement>;
    onFocus?: React.FocusEventHandler<HTMLButtonElement>;
    onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
    onKeyUp?: React.KeyboardEventHandler<HTMLButtonElement>;
    children?: React.ReactNode;
};

const renderOption = (option: NormalizedOption, key: string) => {
    return (
        <SelectItem key={key} value={option.value} disabled={option.disabled} className="flos-custom-select-item">
            {option.label}
        </SelectItem>
    );
};

const renderOptionGroups = (groups: NormalizedOptionGroups) => {
    return groups.flatMap((group) => {
        if (group.label) {
            return (
                <SelectPrimitive.Group key={group.key} className="flos-custom-select-group">
                    <SelectPrimitive.Label className="flos-custom-select-group-label">{group.label}</SelectPrimitive.Label>
                    {group.options.map((option) => renderOption(option, `${group.key}-${option.value}`))}
                </SelectPrimitive.Group>
            );
        }

        return group.options.map((option, index) => renderOption(option, `${group.key}-${index}-${option.value}`));
    });
};

const mergeRefs = <T extends HTMLElement>(...refs: Array<React.ForwardedRef<T>>): React.RefCallback<T> => {
    return (node: T | null) => {
        refs.forEach((ref) => {
            if (!ref) return;
            if (typeof ref === 'function') {
                ref(node);
            } else if (ref && typeof ref === 'object') {
                (ref as React.MutableRefObject<T | null>).current = node;
            }
        });
    };
};

const SelectInput = React.forwardRef<HTMLButtonElement, SelectInputProps>((props, forwardedRef) => {
    const {
        children,
        placeholder,
        options,
        label,
        name,
        id,
        className,
        optionGroupStrategy,
        onSelectionChange,
        onChange,
        onValueChange,
        onBlur,
        onFocus,
        onKeyDown,
        onKeyUp,
        'aria-describedby': ariaDescribedBy,
        ...rootProps
    } = props;

    const normalizedGroups = React.useMemo(() => (options ? normalizeOptionGroup(options, optionGroupStrategy) : []), [options, optionGroupStrategy]);

    const optionElements = React.useMemo(() => renderOptionGroups(normalizedGroups), [normalizedGroups]);
    const flattenedOptions = React.useMemo(() => flattenGroups(normalizedGroups), [normalizedGroups]);

    const handleValueChange = React.useCallback<NonNullable<SelectProps['onValueChange']>>(
        (value) => {
            const option = flattenedOptions.find((candidate) => candidate.value === value);
            const payload: SelectChangeDetails = {
                value,
                name,
                option,
            };

            onSelectionChange?.(payload);
            onChange?.(payload);
            onValueChange?.(value);
        },
        [flattenedOptions, name, onSelectionChange, onChange, onValueChange]
    );

    const combinedRef = React.useMemo(() => mergeRefs(forwardedRef), [forwardedRef]);

    return (
        <SelectPrimitive.Root {...rootProps} name={name} onValueChange={handleValueChange}>
            <SelectTrigger
                ref={combinedRef}
                id={id}
                hasLabel={!!label}
                placeholder={placeholder}
                className={className}
                ariaDescribedBy={ariaDescribedBy}
                onBlur={onBlur}
                onFocus={onFocus}
                onKeyDown={onKeyDown}
                onKeyUp={onKeyUp}
            />
            <SelectDropdownContent>{options ? optionElements : children}</SelectDropdownContent>
        </SelectPrimitive.Root>
    );
});

SelectInput.displayName = 'SelectInput';

export type { SelectInputProps, SelectChangeDetails, SelectChangeHandler, OptionProp, Options, OptionGroup, OptionsGroup };
export { SelectInput, SelectItem, normalizeOption, normalizeOptionGroup, normalizeOptions, groupNormalizedOptions, flattenGroups };
export default SelectInput;
